import apiClient from './api-client';

// API Path Constants
const API_BASE = '/masterdata/purposes';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    TRANSLATIONS: (purposeId: number) => `${API_BASE}/${purposeId}/translations`,
    PREFERENCES: (purposeId: number) => `${API_BASE}/${purposeId}/preferences`,
    PROCESSORS: (purposeId: number) => `${API_BASE}/${purposeId}/processors`,
    DATA_ELEMENTS: (purposeId: number) => `${API_BASE}/${purposeId}/data-elements`,
    PUBLISH: (purposeId: number) => `${API_BASE}/${purposeId}/publish`,
} as const;

// Server Response Interfaces (based on actual API response)
export interface ServerPurpose {
    PurposeId: number;
    ClientId: number;
    Name: string;
    Description: string;
    CreatedBy: number;
    CreatedDate: string;
    ModifiedDate: string | null;
    ModifiedBy: number | null;
    IsActive: boolean;
    DepartmentId: number;
    IsPublished: boolean;
    DepartmentName?: string;
    mappings?: {
        preferencesCount: number;
        processorsCount: number;
        dataElementsCount: number;
    };
    translations?: ServerPurposeTranslation[];
}

export interface ServerPurposeTranslation {
    TranslationId: number;
    PurposeId: number;
    LanguageCode: string;
    TranslatedName: string;
    TranslatedDescription: string;
    CreatedBy: number;
    CreatedDate: string;
    ModifiedDate: string | null;
    ModifiedBy: number | null;
}

export interface ServerPurposeResponse {
    success: boolean;
    data: ServerPurpose[];
    pagination?: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}
// by ID
export interface ServerPurposeDetailResponse {
    success: boolean;
    data: ServerPurpose;
}

// Client-side Interfaces (mapped from server response)
export interface Purpose {
    purposeId: number;
    clientId: number;
    departmentId: number;
    departmentName?: string;
    name: string;
    description: string;
    isPublished: boolean;
    createdBy: number;
    createdDate: string;
    modifiedDate: string | null;
    modifiedBy: number | null;
    isActive: boolean;
    mappings?: {
        preferencesCount: number;
        processorsCount: number;
        dataElementsCount: number;
    };
    translations?: PurposeTranslation[];
}

export interface PurposeQueryParams {
    filters?: {
        isPublished?: boolean;
        departmentId?: number;
    };
    pagination?: {
        page: number;
        pageSize: number;
    };
    sortBy?: 'a-z' | 'z-a' | 'newest' | 'oldest' | 'A-Z' | 'Z-A' | 'Newest' | 'Oldest';
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface PurposeTranslation {
    translationId?: number;
    purposeId: number;
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
    createdBy?: number;
    createdDate?: string;
    modifiedDate?: string | null;
    modifiedBy?: number | null;
}

export interface PurposePreferenceMapping {
    mappingId: number;
    purposeId: number;
    purposePreferenceId: number;
    createdBy: number;
    createdDate: string;
    modifiedDate?: string | null;
    modifiedBy?: number | null;
    preference: {
        name: string;
        description?: string;
        displayAs: 'BUTTON' | 'CHECKBOX';
        allowMultipleSelections: boolean;
        setADefaultSelection: boolean;
        isPublished: boolean;
    };
}

export interface PurposeProcessorMapping {
    mappingId: number;
    purposeId: number;
    processorId: number;
    createdBy: number;
    createdDate: string;
    modifiedDate?: string | null;
    modifiedBy?: number | null;
    processor: {
        name: string;
        description?: string;
        type: string;
        website?: string;
        contactEmail?: string;
        contactPhone?: string;
        isPublished: boolean;
    };
}

export interface PurposeDataElementMapping {
    mappingId: number;
    purposeId: number;
    elementId: number;
    createdBy: number;
    createdDate: string;
    modifiedDate?: string | null;
    modifiedBy?: number | null;
    element: {
        name: string;
        description?: string;
        elementCode: string;
        type: string;
        isIdentifier: boolean;
        isPersonalData: boolean;
        validationRule?: string;
        isMultiSelect: boolean;
        isPublished: boolean;
    };
}

export interface Department {
    departmentId: number;
    name: string;
    description?: string;
    isActive: boolean;
    createdBy: number;
    createdDate: string;
    modifiedBy: number | null;
    modifiedDate: string | null;
}

// Service methods
export const purposeService = {
    getPurposes: async (queryParams?: PurposeQueryParams): Promise<PaginatedResponse<Purpose>> => {
        const queryParamsObj = new URLSearchParams();
        
        if (queryParams?.filters) {
            if (queryParams.filters.isPublished !== undefined) {
                queryParamsObj.append('isPublished', queryParams.filters.isPublished.toString());
            }
            if (queryParams.filters.departmentId) {
                queryParamsObj.append('departmentId', queryParams.filters.departmentId.toString());
            }
        }
        if (queryParams?.sortBy) {
            queryParamsObj.append('sortBy', queryParams.sortBy);
        }
        if (queryParams?.pagination) {
            queryParamsObj.append('page', queryParams.pagination.page.toString());
            queryParamsObj.append('pageSize', queryParams.pagination.pageSize.toString());
        }

        const response = await apiClient.get<ServerPurposeResponse>(`${API_PATHS.BASE}?${queryParamsObj.toString()}`);
        
        // Map server response to client interface
        const mappedData: Purpose[] = response.data.map(serverPurpose => ({
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings
        }));
        
        return {
            data: mappedData,
            total: response.pagination?.total || mappedData.length,
            page: response.pagination?.page || 1,
            pageSize: response.pagination?.pageSize || mappedData.length,
            totalPages: response.pagination?.totalPages || 1
        };
    },

    getPurposeById: async (purposeId: number): Promise<Purpose | undefined> => {
        const response = await apiClient.get<ServerPurposeDetailResponse>(API_PATHS.BY_ID(purposeId));
        
        const serverPurpose = response.data;
        return {
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings,
            translations: serverPurpose.translations?.map(translation => ({
                translationId: translation.TranslationId,
                purposeId: translation.PurposeId,
                languageCode: translation.LanguageCode,
                translatedName: translation.TranslatedName,
                translatedDescription: translation.TranslatedDescription,
                createdBy: translation.CreatedBy,
                createdDate: translation.CreatedDate,
                modifiedDate: translation.ModifiedDate,
                modifiedBy: translation.ModifiedBy
            }))
        };
    },

    createPurpose: async (purpose: {
        departmentId: number;
        name: string;
        description?: string;
    }): Promise<Purpose> => {
        // Convert to server format (PascalCase)
        const serverPayload = {
            DepartmentId: purpose.departmentId,
            Name: purpose.name,
            Description: purpose.description || ''
        };

        const response = await apiClient.post<ServerPurposeDetailResponse>(API_PATHS.BASE, serverPayload);
        
        const serverPurpose = response.data;
        return {
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings
        };
    },

    updatePurpose: async (purposeId: number, updates: Partial<Purpose>): Promise<Purpose> => {
        const response = await apiClient.put<ServerPurposeDetailResponse>(API_PATHS.BY_ID(purposeId), updates);
        
        const serverPurpose = response.data;
        return {
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings
        };
    },

    deletePurpose: async (purposeId: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(API_PATHS.BY_ID(purposeId));
    },

    publishPurpose: async (purposeId: number): Promise<Purpose> => {
        const response = await apiClient.patch<ServerPurposeDetailResponse>(
            API_PATHS.PUBLISH(purposeId),
            { isPublished: true }
        );
        
        const serverPurpose = response.data;
        return {
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings
        };
    },

    unpublishPurpose: async (purposeId: number): Promise<Purpose> => {
        const response = await apiClient.patch<ServerPurposeDetailResponse>(
            API_PATHS.PUBLISH(purposeId),
            { isPublished: false }
        );
        
        const serverPurpose = response.data;
        return {
            purposeId: serverPurpose.PurposeId,
            clientId: serverPurpose.ClientId,
            departmentId: serverPurpose.DepartmentId,
            departmentName: serverPurpose.DepartmentName,
            name: serverPurpose.Name,
            description: serverPurpose.Description,
            isPublished: serverPurpose.IsPublished,
            createdBy: serverPurpose.CreatedBy,
            createdDate: serverPurpose.CreatedDate,
            modifiedDate: serverPurpose.ModifiedDate,
            modifiedBy: serverPurpose.ModifiedBy,
            isActive: serverPurpose.IsActive,
            mappings: serverPurpose.mappings
        };
    },

    updateTranslation: async (purposeId: number, translation: {
        languageCode: string;
        translatedName: string;
        translatedDescription?: string;
    }): Promise<PurposeTranslation> => {
        // Convert to server format (PascalCase)
        const serverPayload = {
            LanguageCode: translation.languageCode,
            TranslatedName: translation.translatedName,
            TranslatedDescription: translation.translatedDescription || ''
        };

        const response = await apiClient.post<{ success: boolean; data: PurposeTranslation }>(
            API_PATHS.TRANSLATIONS(purposeId),
            serverPayload
        );
        return response.data;
    },

    getPurposePreferences: async (purposeId: number): Promise<PurposePreferenceMapping[]> => {
        const response = await apiClient.get<{ success: boolean; data: any[] }>(
            API_PATHS.PREFERENCES(purposeId)
        );
        
        // Map server response (PascalCase) to client interface (camelCase with nested preference object)
        return response.data.map(serverMapping => ({
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            purposePreferenceId: serverMapping.PurposePreferenceId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            preference: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                displayAs: serverMapping.DisplayAs,
                allowMultipleSelections: serverMapping.AllowMultipleSelections,
                setADefaultSelection: serverMapping.SetADefaultSelection,
                isPublished: true // Assuming published preferences are returned
            }
        }));
    },

    addPurposePreference: async (purposeId: number, preferenceId: number): Promise<PurposePreferenceMapping> => {
        const response = await apiClient.post<{ success: boolean; data: any }>(
            API_PATHS.PREFERENCES(purposeId),
            { PurposePreferenceId: preferenceId }
        );
        
        // Map server response to client interface
        const serverMapping = response.data;
        return {
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            purposePreferenceId: serverMapping.PurposePreferenceId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            preference: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                displayAs: serverMapping.DisplayAs,
                allowMultipleSelections: serverMapping.AllowMultipleSelections,
                setADefaultSelection: serverMapping.SetADefaultSelection,
                isPublished: true
            }
        };
    },

    removePurposePreference: async (purposeId: number, preferenceId: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(
            API_PATHS.PREFERENCES(purposeId),
            { data: { PurposePreferenceId: preferenceId } }
        );
    },

    getPurposeProcessors: async (purposeId: number): Promise<PurposeProcessorMapping[]> => {
        const response = await apiClient.get<{ success: boolean; data: any[] }>(
            API_PATHS.PROCESSORS(purposeId)
        );
        
        // Map server response (PascalCase) to client interface (camelCase with nested processor object)
        return response.data.map(serverMapping => ({
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            processorId: serverMapping.ProcessorId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            processor: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                type: serverMapping.Type,
                website: serverMapping.Website,
                contactEmail: serverMapping.ContactEmail,
                contactPhone: serverMapping.ContactPhone,
                isPublished: true // Assuming published processors are returned
            }
        }));
    },

    addPurposeProcessor: async (purposeId: number, processorId: number): Promise<PurposeProcessorMapping> => {
        const response = await apiClient.post<{ success: boolean; data: any }>(
            API_PATHS.PROCESSORS(purposeId),
            { ProcessorId: processorId }
        );
        
        // Map server response to client interface
        const serverMapping = response.data;
        return {
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            processorId: serverMapping.ProcessorId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            processor: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                type: serverMapping.Type,
                website: serverMapping.Website,
                contactEmail: serverMapping.ContactEmail,
                contactPhone: serverMapping.ContactPhone,
                isPublished: true
            }
        };
    },

    removePurposeProcessor: async (purposeId: number, processorId: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(
            API_PATHS.PROCESSORS(purposeId),
            { data: { processorId: processorId } }
        );
    },

    getPurposeDataElements: async (purposeId: number): Promise<PurposeDataElementMapping[]> => {
        const response = await apiClient.get<{ success: boolean; data: any[] }>(
            API_PATHS.DATA_ELEMENTS(purposeId)
        );
        
        // Map server response (PascalCase) to client interface (camelCase with nested element object)
        return response.data.map(serverMapping => ({
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            elementId: serverMapping.ElementId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            element: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                elementCode: serverMapping.ElementCode,
                type: serverMapping.Type,
                isIdentifier: serverMapping.IsIdentifier,
                isPersonalData: serverMapping.IsPersonalData,
                validationRule: serverMapping.ValidationRule,
                isMultiSelect: serverMapping.IsMultiSelect,
                isPublished: true // Assuming published data elements are returned
            }
        }));
    },

    addPurposeDataElement: async (purposeId: number, elementId: number): Promise<PurposeDataElementMapping> => {
        const response = await apiClient.post<{ success: boolean; data: any }>(
            API_PATHS.DATA_ELEMENTS(purposeId),
            { ElementId: elementId }
        );
        
        // Map server response to client interface
        const serverMapping = response.data;
        return {
            mappingId: serverMapping.MappingId,
            purposeId: serverMapping.PurposeId,
            elementId: serverMapping.ElementId,
            createdBy: serverMapping.CreatedBy,
            createdDate: serverMapping.CreatedDate,
            modifiedDate: serverMapping.ModifiedDate,
            modifiedBy: serverMapping.ModifiedBy,
            element: {
                name: serverMapping.Name,
                description: serverMapping.Description,
                elementCode: serverMapping.ElementCode,
                type: serverMapping.Type,
                isIdentifier: serverMapping.IsIdentifier,
                isPersonalData: serverMapping.IsPersonalData,
                validationRule: serverMapping.ValidationRule,
                isMultiSelect: serverMapping.IsMultiSelect,
                isPublished: true
            }
        };
    },

    removePurposeDataElement: async (purposeId: number, elementId: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(
            API_PATHS.DATA_ELEMENTS(purposeId),
            { data: { elementId: elementId } }
        );
    }
};
